import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Lightbulb, AlertTriangle, TrendingUp, DollarSign, Percent,
  RefreshCw, ChevronRight, Zap, PiggyBank, Target, Building,
  Receipt, AlertCircle, CheckCircle, Info
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function InsightsDashboard() {
  const [insights, setInsights] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('insights');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [insightsRes, oppsRes] = await Promise.all([
        axios.get(`${API}/insights/predictive`, { withCredentials: true }),
        axios.get(`${API}/insights/opportunities`, { withCredentials: true })
      ]);
      setInsights(insightsRes.data);
      setOpportunities(oppsRes.data);
    } catch (error) {
      console.error('Failed to fetch insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      case 'medium': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'low': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <AlertCircle className="w-4 h-4 text-rose-500" />;
      case 'medium': return <Info className="w-4 h-4 text-amber-500" />;
      case 'low': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      default: return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
        {[
          { id: 'insights', label: 'AI Insights', icon: Lightbulb },
          { id: 'opportunities', label: 'Cost Savings', icon: PiggyBank }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${
              activeTab === tab.id
                ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
            }`}
            data-testid={`insights-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Insights Tab */}
      {activeTab === 'insights' && insights && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {insights.message ? (
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 text-center">
              <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 dark:text-slate-400">{insights.message}</p>
            </div>
          ) : (
            <>
              {/* Recommendations */}
              {insights.recommendations?.length > 0 && (
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-100 dark:border-indigo-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold text-indigo-900 dark:text-indigo-200">Recommendations</h3>
                  </div>
                  <div className="space-y-2">
                    {insights.recommendations.map((rec, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-3 bg-white/70 dark:bg-slate-800/50 rounded-lg p-3"
                      >
                        {getPriorityIcon(rec.priority)}
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{rec.action}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{rec.reason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Anomalies */}
              {insights.anomalies?.length > 0 && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-slate-900 dark:text-white">Spending Anomalies</h3>
                  </div>
                  <div className="space-y-3">
                    {insights.anomalies.map((anomaly, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          anomaly.severity === 'high' 
                            ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-700' 
                            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getSeverityColor(anomaly.severity)}`}>
                                {anomaly.severity}
                              </span>
                              <span className="text-xs text-slate-500 capitalize">{anomaly.type.replace('_', ' ')}</span>
                            </div>
                            <p className="text-sm text-slate-900 dark:text-white mt-1.5">{anomaly.insight}</p>
                            {anomaly.recommendation && (
                              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                                <Target className="w-3 h-3" />
                                {anomaly.recommendation}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seasonal Patterns */}
              {insights.seasonal_patterns?.length > 0 && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-slate-900 dark:text-white">Seasonal Patterns</h3>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {insights.seasonal_patterns.map((pattern, idx) => (
                      <div 
                        key={idx}
                        className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-2 h-2 rounded-full ${
                            pattern.impact === 'high' ? 'bg-rose-500' : 'bg-amber-500'
                          }`} />
                          <span className="text-xs font-medium text-slate-500 uppercase">{pattern.quarter}</span>
                        </div>
                        <p className="text-sm text-slate-900 dark:text-white">{pattern.insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tax Insights */}
              {insights.tax_insights?.length > 0 && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Receipt className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-slate-900 dark:text-white">Tax Insights</h3>
                  </div>
                  <div className="space-y-3">
                    {insights.tax_insights.map((tax, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg ${
                          tax.type === 'tax_summary' 
                            ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700' 
                            : 'bg-slate-50 dark:bg-slate-800'
                        }`}
                      >
                        <p className="text-sm text-slate-900 dark:text-white">{tax.insight}</p>
                        {tax.effective_rate && (
                          <div className="flex items-center gap-1 mt-1">
                            <Percent className="w-3 h-3 text-emerald-600" />
                            <span className="text-xs text-emerald-600 font-medium">Effective rate: {tax.effective_rate}</span>
                          </div>
                        )}
                        {tax.recommendation && (
                          <p className="text-xs text-slate-500 mt-1.5">{tax.recommendation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      )}

      {/* Cost Savings Tab */}
      {activeTab === 'opportunities' && opportunities && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Summary */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-100 dark:border-emerald-800 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">Total Spend Analyzed</p>
                <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                  ${opportunities.total_spend.toLocaleString()}
                </p>
              </div>
              <PiggyBank className="w-10 h-10 text-emerald-500" />
            </div>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
              {opportunities.total_analyzed} invoices analyzed
            </p>
          </div>

          {/* Opportunities List */}
          {opportunities.opportunities?.length > 0 ? (
            <div className="space-y-3">
              {opportunities.opportunities.map((opp, idx) => (
                <div 
                  key={idx}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      opp.type === 'vendor_concentration' ? 'bg-purple-100 dark:bg-purple-900/30' :
                      opp.type === 'recurring_vendor' ? 'bg-blue-100 dark:bg-blue-900/30' :
                      'bg-emerald-100 dark:bg-emerald-900/30'
                    }`}>
                      {opp.type === 'vendor_concentration' && <Building className="w-5 h-5 text-purple-600" />}
                      {opp.type === 'recurring_vendor' && <RefreshCw className="w-5 h-5 text-blue-600" />}
                      {opp.type === 'early_payment' && <DollarSign className="w-5 h-5 text-emerald-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{opp.insight}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{opp.opportunity}</p>
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-md text-xs font-medium">
                        <DollarSign className="w-3 h-3" />
                        Potential savings: {opp.potential_savings}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 text-center">
              <PiggyBank className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 dark:text-slate-400">
                No cost-saving opportunities identified yet. Add more invoices for analysis.
              </p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

export default InsightsDashboard;
